// Small pure helpers for bulk send (BULK-02): which files we take, the preview
// state a csvUpload / saveBulkColumnMapping result becomes, the problems CSV,
// and the merge tags of the send page.

// A CSV whatever the browser calls it: Excel on Windows reports .csv files as
// application/vnd.ms-excel, and some browsers report no type at all (BULK-01).
export const isCsvFile = (file: File) =>
    /\.csv$/i.test(file?.name || "") || (file?.type || "").includes("csv");

// The old Excel format (.xls). The backend can't read it, so we refuse it here
// with a message that says how to save it instead.
export const isOldExcelFile = (file: File) => /\.xls$/i.test(file?.name || "");

// A file bulk send reads: CSV, or an Excel workbook (.xlsx).
export const isBulkFile = (file: File) =>
    !!file &&
    !isOldExcelFile(file) &&
    (isCsvFile(file) ||
        /\.xlsx$/i.test(file.name || "") ||
        (file.type || "").includes("spreadsheetml"));

export const BULK_FILE_ACCEPT = ".csv,.xlsx";

export const OLD_EXCEL_MESSAGE =
    'This is an old Excel file (.xls). In Excel, choose File → Save As → "Excel Workbook (.xlsx)" or "CSV UTF-8", then upload that file.';

export const NOT_BULK_FILE_MESSAGE = "Upload a CSV or Excel (.xlsx) file.";

// The message for a file we don't take, or "" when we take it.
export const bulkFileProblem = (file: File) => {
    if (!file) return NOT_BULK_FILE_MESSAGE;
    if (isOldExcelFile(file)) return OLD_EXCEL_MESSAGE;
    return isBulkFile(file) ? "" : NOT_BULK_FILE_MESSAGE;
};

// A JSONString comes as a JSON string; csvUpload's result comes as an object.
export const parseJsonValue = (value: any, fallback: any = null) => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value !== "string") return value;
    try {
        const parsed = JSON.parse(value);
        return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (e) {
        return fallback;
    }
};

export interface BulkPreviewState {
    csvData: any[];
    errors: any[];
    rowErrors: any[];
    warnings: string[];
    columns?: string[];
    unmatchedColumns: string[];
    missingColumns: string[];
    columnOptions: string[];
    mapping: { [fileHeader: string]: string | null };
    // BULK-03: calculated columns, formula inputs that aren't fields, and the
    // values given for everyone.
    computedColumns: string[];
    inputColumns: string[];
    sharedValues: { [name: string]: string };
}

// What the preview page reads from history state, from a csvUpload (or
// saveBulkColumnMapping) result.
export const bulkPreviewState = (rawResult: any): BulkPreviewState => {
    const result = parseJsonValue(rawResult, {}) || {};
    return {
        csvData: result.csv_data || [],
        errors: result.error || [],
        rowErrors: result.errors || [],
        warnings: result.warnings || [],
        columns: result.columns,
        unmatchedColumns: result.unmatchedColumns || [],
        missingColumns: result.missingColumns || [],
        columnOptions: result.columnOptions || [],
        mapping: result.mapping || {},
        computedColumns: result.computedColumns || [],
        inputColumns: result.inputColumns || [],
        sharedValues: result.sharedValues || {},
    };
};

// One CSV cell: quoted, quotes doubled, and a leading = + - @ made harmless
// so a spreadsheet doesn't run it as a formula.
export const csvCell = (value: any) => {
    let text = value === null || value === undefined ? "" : String(value);
    if (/^[=+\-@]/.test(text)) text = "'" + text;
    return '"' + text.replace(/"/g, '""') + '"';
};

export const toCsv = (rows: any[][]) =>
    rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";

// The preview's problems as a CSV: Row, Column, Value, Problem.
export const problemsCsv = (
    errors: { row: any; column?: any; value?: any; message?: any }[]
) =>
    toCsv([
        ["Row", "Column", "Value", "Problem"],
        ...(errors || []).map((e) => [e.row, e.column, e.value, e.message]),
    ]);

export const downloadTextFile = (text: string, fileName: string, type = "text/csv") => {
    // A BOM so Excel reads the file as UTF-8.
    const blob = new Blob(["﻿" + text], { type: `${type};charset=utf-8` });
    const a = window.document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.setAttribute("download", fileName);
    a.click();
};

export const RECIPIENT_NAME = "Recipient Name";
export const RECIPIENT_EMAIL = "Recipient Email";

const normalise = (text: string) =>
    String(text || "").trim().toLowerCase().replace(/\s+/g, " ");

// The tags the send page offers: name and email first, then each column once.
export const mergeTagOptions = (columns: (string | null)[] | null | undefined) => {
    const seen = new Set<string>();
    const tags: string[] = [];
    [RECIPIENT_NAME, RECIPIENT_EMAIL, ...(columns || [])].forEach((column) => {
        if (!column || seen.has(normalise(column))) return;
        seen.add(normalise(column));
        tags.push(column);
    });
    return tags;
};

export const mergeTag = (column: string) => `{{${column}}}`;

// The text with the tag put where the cursor was (or at the end), and the
// cursor position just after it.
export const insertAtCursor = (
    text: string,
    insert: string,
    selectionStart?: number | null,
    selectionEnd?: number | null
) => {
    const value = text || "";
    const start =
        typeof selectionStart === "number" ? Math.min(selectionStart, value.length) : value.length;
    const end =
        typeof selectionEnd === "number" ? Math.max(start, Math.min(selectionEnd, value.length)) : start;
    return {
        text: value.slice(0, start) + insert + value.slice(end),
        cursor: start + insert.length,
    };
};

const MERGE_TAG = /\{\{\s*([^{}]+?)\s*\}\}/g;

// The text as one row would get it, the way the backend fills it (a tag that
// isn't a column of the row stays as typed).
export const fillMergeTags = (text: string, row: { [header: string]: any } | null | undefined) => {
    if (!text || !row) return text;
    const cells: { [key: string]: any } = {};
    Object.keys(row).forEach((header) => {
        cells[normalise(header)] = row[header];
    });
    return text.replace(MERGE_TAG, (match, name) => {
        const value = cells[normalise(name)];
        return value === undefined || value === null ? match : String(value);
    });
};

export interface FailedRow {
    row?: number;
    email?: string;
    error?: string;
}

export const parseFailedRows = (value: any): FailedRow[] => {
    const rows = parseJsonValue(value, []);
    return Array.isArray(rows) ? rows : [];
};

// The per-person status shown on the bulk send's results.
export type RecipientStatus = "SENT" | "VIEWED" | "SIGNED" | "REJECTED" | "EXPIRED";

// From the signer's status (1/2 pending, 3 signed, 4 viewed, 5 rejected) and
// the document's (9 expired, 7 rejected), when the backend gives no
// `recipients` list. An expired document is Expired, not Pending.
export const statusFromSignee = (signeeStatus: number, documentStatus: number): RecipientStatus => {
    if (signeeStatus === 3) return "SIGNED";
    if (signeeStatus === 5 || documentStatus === 7) return "REJECTED";
    if (documentStatus === 9) return "EXPIRED";
    if (signeeStatus === 4) return "VIEWED";
    return "SENT";
};
